/**
 * Server-state hooks for notification preferences (C16 API) via TanStack Query.
 *
 * Global preferences and per-search overrides come from a single GET; toggling
 * a channel PATCHes only the changed field (channels are independent) and
 * invalidates the cache so the UI reflects the persisted state.
 */
import {
  type NotificationPreferences,
  type NotificationPreferencesResponse,
  type UpdateNotificationPreferencesRequest,
} from "@records-monitor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/api/useApiClient";

export const preferencesKey = ["notifications", "preferences"] as const;

/** GET /notifications/preferences — global + per-search overrides. */
export function useNotificationPreferences() {
  const api = useApiClient();
  return useQuery({
    queryKey: preferencesKey,
    queryFn: () => api.get<NotificationPreferencesResponse>("/notifications/preferences"),
  });
}

/** PATCH /notifications/preferences — update one or more global channels. */
export function useUpdateGlobalPreferences() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateNotificationPreferencesRequest) =>
      api.patch<NotificationPreferences>("/notifications/preferences", patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preferencesKey });
    },
  });
}

/** PATCH /notifications/preferences/search/:id — per-search override. */
export function useUpdateSearchPreferences(savedSearchId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateNotificationPreferencesRequest) =>
      api.patch<NotificationPreferences>(
        `/notifications/preferences/search/${savedSearchId}`,
        patch,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preferencesKey });
    },
  });
}
